<template>
  <v-off-canvas
    id="the-case-resource-update-price-off-canvas"
    :backdrop-static="true"
    offcanvas-class="offcanvas-end"
    @closed="close"
  >
    <resizable-container>
      <off-canvas-header>
        <h5 class="offcanvas-title">{{ $t("tourPricing.invoice.extraCharge") }}</h5>
      </off-canvas-header>

      <off-canvas-body>
        <div>
          <form id="case-invoice-update-form" class="d-flex flex-column justify-content-between h-100">
            <v-form-input
              id="invoice-description"
              v-model="invoiceDescription"
              :label="$t('tourPricing.invoice.invoiceDescription')"
              autocomplete="off"
              form-type="outline"
              :yup-errors-variable="errors.invoiceDescription"
            />
            <v-form-input
              id="case-invoice-amount"
              v-model="amount"
              :label="$t('general.shared.amount')"
              autocomplete="off"
              :yup-errors-variable="errors.amount"
              form-type="outline"
            />
            <div class="mb-16 pt-16">
              <div class="fw-bold mb-8">Following resources will be affected:</div>

              <ul class="list-unstyled ps-12">
                <li class="mb-6" v-for="(r, idx) in rooms" :key="idx">
                  <span class="fw-semibold">{{ idx + 1 }}.</span>
                  <span class="fw-semibold ms-8">{{ r.name }} :</span>
                  <span class="fw-semibold ms-8"><v-icon icon="euro" size="xs" /> {{ r.price }}</span>
                </li>
              </ul>
            </div>
            <v-button
              :disabled="!meta.valid"
              class="btn-submit-form btn-icon w-fit align-self-end mt-16"
              size="sm"
              variant="primary"
              @click="submitForm"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </form>
        </div>
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script setup lang="ts" name="TheCaseResourceUpdatePriceOffCanvas">
import VOffCanvas from "@/components/vendor/basic/offcanvas/VOffCanvas.vue";
import OffCanvasHeader from "@/components/vendor/basic/offcanvas/OffCanvasHeader.vue";
import OffCanvasBody from "@/components/vendor/basic/offcanvas/OffCanvasBody.vue";
import ResizableContainer from "@/components/resizablecontainer/ResizableContainer.vue";
import { VFormInput } from "@uniqoders/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import api from "@/api";
import { useI18n } from "vue-i18n";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { POSITION, useToast } from "vue-toastification";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  caseId: {
    type: Number,
    required: true,
  },
  rooms: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const emit = defineEmits(["closed", "reloadItems"]);

const { t } = useI18n();
const toast = useToast();

const schema = yup.object({
  invoiceDescription: yup.string().required().nullable().label(t("tourPricing.invoice.invoiceDescription")),
  amount: yup
    .number()
    .required()
    .nullable()
    .min(0)
    // eslint-disable-next-line no-restricted-globals
    .transform((value) => (isNaN(value) ? undefined : value))
    .label(t("tourPricing.invoice.number")),
});

const { handleSubmit, errors, meta } = useForm({
  validationSchema: schema,
});

const { value: invoiceDescription } = useField<string>("invoiceDescription");
const { value: amount } = useField<number | null>("amount");

const submitForm = handleSubmit(async () => {
  try {
    const payload = {
      descriptionLine: invoiceDescription.value,
      amount: amount.value,
      resourceIds: props.rooms.map((r: any) => r.id)?.flat(),
    };
    const response = await api.case.addExtraInvoice(props.agency, props.caseId, payload);
    toast.success(response?.data?.message || "Successfully updated", { position: POSITION.BOTTOM_RIGHT });
    emit("reloadItems");
    close();
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data?.message || "Something went wrong", { position: POSITION.BOTTOM_RIGHT });
  }
});

const close = () => {
  emit("closed");
};
</script>
